/**
 * Community Engagement Agent
 * Manages social features, user interactions, and community building
 */

interface UserInteraction {
  userId: string;
  type: 'review' | 'favorite' | 'visit' | 'share' | 'comment';
  targetId: string;
  targetType: 'farm' | 'product' | 'recipe' | 'event';
  timestamp: Date;
  data?: any;
}

interface CommunityStats {
  totalMembers: number;
  activeThisMonth: number;
  totalReviews: number;
  totalFavorites: number;
  engagementRate: number;
}

interface UserProfile {
  userId: string;
  name: string;
  memberSince: Date;
  favoriteFarms: string[];
  favoriteProducts: string[];
  reviewCount: number;
  badges: string[];
  contributionScore: number;
}

interface ContentRecommendation {
  type: 'farm' | 'event' | 'recipe' | 'article';
  id: string;
  title: string;
  reason: string;
  score: number;
}

class CommunityEngagementAgent {
  private interactions: UserInteraction[] = [];
  private users: Map<string, UserProfile> = new Map();

  /**
   * Track user interaction
   * @param interaction User interaction data
   */
  trackInteraction(interaction: UserInteraction): void {
    this.interactions.push(interaction);
    this.updateUserProfile(interaction);
  }

  /**
   * Get community statistics
   * @returns Community stats
   */
  getCommunityStats(): CommunityStats {
    const now = new Date();
    const monthAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);

    const activeUsers = new Set(
      this.interactions
        .filter(i => i.timestamp >= monthAgo)
        .map(i => i.userId)
    );

    const totalReviews = this.interactions.filter(i => i.type === 'review').length;
    const totalFavorites = this.interactions.filter(i => i.type === 'favorite').length;

    return {
      totalMembers: this.users.size,
      activeThisMonth: activeUsers.size,
      totalReviews,
      totalFavorites,
      engagementRate: this.users.size > 0 ? (activeUsers.size / this.users.size) * 100 : 0,
    };
  }

  /**
   * Get personalized content recommendations
   * @param userId User ID
   * @param limit Maximum recommendations
   * @returns Content recommendations
   */
  getRecommendations(userId: string, limit: number = 5): ContentRecommendation[] {
    const user = this.users.get(userId);
    if (!user) return [];

    const recommendations: ContentRecommendation[] = [];

    // Recommend farms based on favorite products
    for (const product of user.favoriteProducts.slice(0, 3)) {
      recommendations.push({
        type: 'farm',
        id: `farm-${product}`,
        title: `Farms specializing in ${product}`,
        reason: `Based on your interest in ${product}`,
        score: 90,
      });
    }

    // Recommend events near favorite farms
    for (const farmId of user.favoriteFarms.slice(0, 2)) {
      recommendations.push({
        type: 'event',
        id: `event-${farmId}`,
        title: 'Upcoming Farm Events',
        reason: 'Events at your favorite farms',
        score: 85,
      });
    }

    return recommendations
      .sort((a, b) => b.score - a.score)
      .slice(0, limit);
  }

  /**
   * Award badges to users based on activity
   * @param userId User ID
   * @returns Newly earned badges
   */
  awardBadges(userId: string): string[] {
    const user = this.users.get(userId);
    if (!user) return [];

    const newBadges: string[] = [];

    // Review badges
    if (user.reviewCount >= 1 && !user.badges.includes('First Review')) {
      newBadges.push('First Review');
    }
    if (user.reviewCount >= 10 && !user.badges.includes('Reviewer')) {
      newBadges.push('Reviewer');
    }
    if (user.reviewCount >= 50 && !user.badges.includes('Super Reviewer')) {
      newBadges.push('Super Reviewer');
    }

    // Favorite badges
    if (user.favoriteFarms.length >= 5 && !user.badges.includes('Farm Explorer')) {
      newBadges.push('Farm Explorer');
    }

    // Loyalty badge
    const memberDays = Math.floor(
      (Date.now() - user.memberSince.getTime()) / (24 * 60 * 60 * 1000)
    );
    if (memberDays >= 365 && !user.badges.includes('One Year Member')) {
      newBadges.push('One Year Member');
    }

    user.badges.push(...newBadges);
    return newBadges;
  }

  /**
   * Get trending content
   * @param days Number of days to analyze
   * @returns Trending items
   */
  getTrending(days: number = 7): Array<{
    type: string;
    id: string;
    interactionCount: number;
    trendScore: number;
  }> {
    const cutoff = new Date(Date.now() - days * 24 * 60 * 60 * 1000);
    const recent = this.interactions.filter(i => i.timestamp >= cutoff);

    const counts = new Map<string, number>();
    for (const interaction of recent) {
      const key = `${interaction.targetType}:${interaction.targetId}`;
      counts.set(key, (counts.get(key) || 0) + 1);
    }

    const trending = Array.from(counts.entries())
      .map(([key, count]) => {
        const [type, id] = key.split(':');
        return {
          type,
          id,
          interactionCount: count,
          trendScore: count * (7 / days), // Normalize by time period
        };
      })
      .sort((a, b) => b.trendScore - a.trendScore)
      .slice(0, 10);

    return trending;
  }

  /**
   * Get user leaderboard
   * @param limit Number of top users
   * @returns Top contributors
   */
  getLeaderboard(limit: number = 10): UserProfile[] {
    return Array.from(this.users.values())
      .sort((a, b) => b.contributionScore - a.contributionScore)
      .slice(0, limit);
  }

  // Helper methods

  private updateUserProfile(interaction: UserInteraction): void {
    let user = this.users.get(interaction.userId);

    if (!user) {
      user = {
        userId: interaction.userId,
        name: `User ${interaction.userId}`,
        memberSince: new Date(),
        favoriteFarms: [],
        favoriteProducts: [],
        reviewCount: 0,
        badges: [],
        contributionScore: 0,
      };
      this.users.set(interaction.userId, user);
    }

    // Update stats based on interaction type
    if (interaction.type === 'review') {
      user.reviewCount++;
      user.contributionScore += 10;
    } else if (interaction.type === 'favorite') {
      user.contributionScore += 2;
      if (interaction.targetType === 'farm') {
        user.favoriteFarms.push(interaction.targetId);
      } else if (interaction.targetType === 'product') {
        user.favoriteProducts.push(interaction.targetId);
      }
    } else if (interaction.type === 'share') {
      user.contributionScore += 5;
    }

    this.awardBadges(interaction.userId);
  }
}

export const communityEngagementAgent = new CommunityEngagementAgent();
export default communityEngagementAgent;
